import React, { useState } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getLoginRedirectPath } from './environmentUtils';

/**
 * Auth + role guard wrapper for route elements.
 *
 * Data flow:
 * - Reads `students.isAuthenticated`, `students.authInitialized`, and `students.user` from Redux.
 * - While auth bootstrap is in progress, renders a full-screen loading indicator.
 * - Once initialized:
 *   - unauthenticated users are redirected to `/`
 *   - authenticated users with wrong role for the current route prefix are
 *     redirected to their own dashboard
 *   - authenticated users with correct role see the protected children
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element|null}
 */

const ROLE_ROUTE_PREFIX = {
    admin: '/Admin-Dashboard',
    student: '/Student-dashboard',
    teacher: '/Teacher-dashboard',
    accountant: '/Accountant-Dashboard',
    counsellor: '/Counsellor-Dashboard',
};

const ROLE_HOME = {
    admin: '/Admin-Dashboard/Dashboard',
    student: '/Student-dashboard/dash',
    teacher: '/Teacher-dashboard/dash',
    accountant: '/Accountant-Dashboard/dash',
    counsellor: '/Counsellor-Dashboard/Dashboard',
};

const getCurrentRouteTarget = (location) => {
    const hash = location.hash || window.location.hash || '';
    const hashPath = hash.replace(/^#/, '');
    if (hashPath && hashPath !== '/') return hashPath;
    return `${location.pathname || '/'}${location.search || ''}`;
};

const buildLoginRedirectPath = (location) => {
    const loginPath = getLoginRedirectPath();
    const target = getCurrentRouteTarget(location);
    if (!target || target === '/' || target.startsWith(loginPath)) return loginPath;
    const separator = loginPath.includes('?') ? '&' : '?';
    return `${loginPath}${separator}redirect=${encodeURIComponent(target)}`;
};

const normalizeRole = (value) => String(value || '').trim().toLowerCase();

const isRoleRoutePath = (currentPath, prefix) =>
    currentPath === prefix ||
    currentPath.startsWith(`${prefix}/`) ||
    currentPath.startsWith(`${prefix}?`);

const ProtectedRoute = ({ children, requiredRole }) => {
    const { t } = useTranslation('global');
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useSelector((state) => state.students.isAuthenticated);
    const authInitialized = useSelector((state) => state.students.authInitialized);
    const user = useSelector((state) => state.students.user);
    const [loadTimeout, setLoadTimeout] = useState(false);

    // Timeout: if auth init takes longer than 10s, show error with retry
    useEffect(() => {
        if (authInitialized) return;
        const timer = setTimeout(() => setLoadTimeout(true), 10000);
        return () => clearTimeout(timer);
    }, [authInitialized]);

    useEffect(() => {
        if (!authInitialized) return;

        // Redirect unauthenticated users to login and preserve the protected URL.
        if (!isAuthenticated) {
            navigate(buildLoginRedirectPath(location), { replace: true });
            return;
        }

        // Role-based route guard: prevent accessing other role's dashboard
        const userType = normalizeRole(user?.UserType);
        if (requiredRole && userType !== normalizeRole(requiredRole)) {
            navigate(ROLE_HOME[userType] || '/', { replace: true });
            return;
        }
        if (userType && ROLE_ROUTE_PREFIX[userType]) {
            const hash = location.hash || window.location.hash || '';
            const currentPath = hash.replace(/^#/, '') || location.pathname;

            // Check if user is trying to access a different role's dashboard
            for (const [role, prefix] of Object.entries(ROLE_ROUTE_PREFIX)) {
                if (role !== userType && isRoleRoutePath(currentPath, prefix)) {
                    navigate(ROLE_HOME[userType] || '/');
                    return;
                }
            }
        }
    }, [authInitialized, isAuthenticated, navigate, user, location, requiredRole]);

    // Show loading state until auth is initialized
    if (!authInitialized) {
        // BUG-032: If loading takes too long, show error with retry button
        if (loadTimeout) {
            return (
                <div style={{ textAlign: 'center', padding: '80px 20px' }}>
                    <p>{t('protectedRoute.loadingTimeout', 'Loading is taking longer than expected.')}</p>
                    <button onClick={() => window.location.reload()}
                        style={{ padding: '10px 24px', cursor: 'pointer', background: '#4A90D9', color: '#fff', border: 'none', borderRadius: '6px' }}>
                        {t('protectedRoute.retry', 'Retry')}
                    </button>
                </div>
            );
        }

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh',
                backgroundColor: '#F8FAFC',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #E2E8F0',
                    borderTop: '4px solid #113F67',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }} />
                <p style={{
                    marginTop: '16px',
                    color: '#475569',
                    fontSize: '16px',
                    fontWeight: '500'
                }}>
                    {t('protectedRoute.loading', 'Loading...')}
                </p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Synchronous role-based guard: block render for wrong-role dashboards
    // before useEffect fires, preventing 1-frame flash of protected content.
    if (isAuthenticated) {
        const userType = normalizeRole(user?.UserType);
        if (requiredRole && userType !== normalizeRole(requiredRole)) {
            return null;
        }
        if (userType) {
            const hash = location.hash || window.location.hash || '';
            const currentPath = hash.replace(/^#/, '') || location.pathname;
            for (const [role, prefix] of Object.entries(ROLE_ROUTE_PREFIX)) {
                if (role !== userType && isRoleRoutePath(currentPath, prefix)) {
                    return null; // useEffect will handle the redirect
                }
            }
        }
    }

    // Return children only if authenticated
    return authInitialized && isAuthenticated ? children : null;
};

export default ProtectedRoute;
